import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Loader2, ScanSearch, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RelativeTime, formatNumber } from '../../../components/design-system/admin';
import type { RetailerNetworkSummary } from '../../../common/hooks/use-network-api.hook';

export type ScanUiState = 'idle' | 'starting' | 'running' | 'unavailable';

/**
 * The first thing a retailer reads on the page: how much of their catalogue
 * is already in the network, and how much is waiting for them.
 *
 * The number leads because it is the news — "214 of your products are already
 * visualizer-ready elsewhere" is what makes joining worth a click. The scan
 * button sits beside it and says honestly when it cannot run.
 */
export const NetworkHero: React.FC<{
    summary: RetailerNetworkSummary | null;
    loading: boolean;
    scan: ScanUiState;
    onScan: () => void;
    onReviewMatches: () => void;
    onBrowse: () => void;
}> = ({ summary, loading, scan, onScan, onReviewMatches, onBrowse }) => {
    const { t } = useTranslation();

    if (loading && !summary) {
        return (
            <section className="rounded-xl border bg-card p-6" data-testid="network-hero-loading">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="mt-3 h-7 w-3/4 max-w-md" />
                <Skeleton className="mt-2 h-4 w-1/2 max-w-sm" />
                <div className="mt-6 grid grid-cols-2 gap-4 border-t pt-4 sm:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-10" />
                    ))}
                </div>
            </section>
        );
    }
    if (!summary) return null;

    const linked = summary.linked.variants;
    const unavailable = scan === 'unavailable' || !summary.matcherAvailable;
    const running = scan === 'starting' || scan === 'running' || summary.lastScan?.status === 'running';

    const headline =
        linked > 0
            ? t('NETWORK_RETAILER.HERO.IN_NETWORK', { count: linked, formatted: formatNumber(linked) })
            : summary.suggested > 0
              ? t('NETWORK_RETAILER.HERO.MAYBE_IN_NETWORK', { count: summary.suggested, formatted: formatNumber(summary.suggested) })
              : t('NETWORK_RETAILER.HERO.NONE_YET');

    return (
        <section className="rounded-xl border bg-card p-6" data-testid="network-hero">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                <div className="max-w-2xl">
                    <p className="text-xs text-muted-foreground">{t('NETWORK_RETAILER.TITLE')}</p>
                    <h2 className="mt-1 font-heading text-[22px] leading-tight font-medium tracking-tight" data-testid="network-hero-headline">
                        {headline}
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">{t('NETWORK_RETAILER.HERO.EXPLAIN')}</p>
                    {summary.suggested > 0 && (
                        <Button variant="link" className="mt-1 h-auto px-0 text-sm" onClick={onReviewMatches} data-testid="network-hero-review">
                            {t('NETWORK_RETAILER.HERO.REVIEW', { count: summary.suggested })}
                        </Button>
                    )}
                </div>

                <div className="flex shrink-0 flex-col items-start gap-2 md:items-end">
                    <Button variant="outline" onClick={onScan} disabled={running || unavailable} data-testid="network-scan">
                        {running ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <ScanSearch data-icon="inline-start" />}
                        {running ? t('NETWORK_RETAILER.SCAN.RUNNING') : t('NETWORK_RETAILER.SCAN.BUTTON')}
                    </Button>
                    <ScanLine summary={summary} unavailable={unavailable} running={running} />
                </div>
            </div>

            <dl className="mt-6 grid grid-cols-2 gap-4 border-t pt-4 sm:grid-cols-4">
                <Fact label={t('NETWORK_RETAILER.HERO.FACT_LINKED')} value={formatNumber(linked)} testId="network-fact-linked" />
                <Fact label={t('NETWORK_RETAILER.HERO.FACT_SUGGESTED')} value={formatNumber(summary.suggested)} testId="network-fact-suggested" />
                <Fact label={t('NETWORK_RETAILER.HERO.FACT_BRANDS')} value={formatNumber(summary.brandsCarried)} />
                <div>
                    <dt className="text-xs text-muted-foreground">{t('NETWORK_RETAILER.HERO.FACT_AVAILABLE')}</dt>
                    <dd className="mt-0.5 flex items-baseline gap-2">
                        <span className="text-lg font-medium tabular-nums">{formatNumber(summary.available)}</span>
                        {summary.available > 0 && (
                            <Button variant="link" className="h-auto px-0 text-xs" onClick={onBrowse}>
                                {t('NETWORK_RETAILER.HERO.BROWSE')}
                            </Button>
                        )}
                    </dd>
                </div>
            </dl>
        </section>
    );
};

/** Under the button: when the catalogue was last compared, or why it cannot be. */
const ScanLine: React.FC<{ summary: RetailerNetworkSummary; unavailable: boolean; running: boolean }> = ({ summary, unavailable, running }) => {
    const { t } = useTranslation();
    const last = summary.lastScan;
    if (unavailable) {
        return (
            <p className="flex max-w-xs items-start gap-1.5 text-xs text-muted-foreground md:text-end" data-testid="network-scan-unavailable">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                {t('NETWORK_RETAILER.SCAN.UNAVAILABLE')}
            </p>
        );
    }
    if (running) return <p className="text-xs text-muted-foreground">{t('NETWORK_RETAILER.SCAN.RUNNING_HINT')}</p>;
    if (last?.status === 'failed') {
        return (
            <p className="flex max-w-xs items-start gap-1.5 text-xs text-destructive md:text-end" data-testid="network-scan-failed">
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                {t('NETWORK_RETAILER.SCAN.FAILED', { error: last.error || '' })}
            </p>
        );
    }
    if (last?.finishedAt) {
        return (
            <p className="text-xs text-muted-foreground">
                {t('NETWORK_RETAILER.SCAN.LAST')} <RelativeTime value={last.finishedAt} />
                {last.result ? ` · ${t('NETWORK_RETAILER.SCAN.FOUND', { count: last.result.suggested + last.result.confirmed })}` : ''}
            </p>
        );
    }
    return <p className="text-xs text-muted-foreground">{t('NETWORK_RETAILER.SCAN.NEVER')}</p>;
};

const Fact: React.FC<{ label: string; value: string; testId?: string }> = ({ label, value, testId }) => (
    <div data-testid={testId}>
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 text-lg font-medium tabular-nums">{value}</dd>
    </div>
);
